import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import filter from 'lodash-es/filter';
import some from 'lodash-es/some';
import { v4 as v4uuid } from 'uuid';

import { ConfigService } from '../config/config.service';
import { WindowService } from '../window/window.service';
import { FlashMessage, Severity } from './flash-message.model';

@Injectable()
export class FlashMessagesService {
    private static flashMessages: Array<FlashMessage> = [];

    constructor(private configService: ConfigService,
                private windowService: WindowService) {
    }

    // noinspection JSMethodCanBeStatic
    get flashMessages(): FlashMessage[] {
        return FlashMessagesService.flashMessages;
    }

    addMessage(severity: Severity, message: string, preface: string = ''): string {
        const flashMessage = {
            id: v4uuid(),
            text: message,
            preface: preface,
            severity: severity
        } as FlashMessage;
        this.windowService.scrollToTop();
        FlashMessagesService.flashMessages = this.flashMessages.concat(flashMessage);
        return flashMessage.id;
    }

    addMessageFor<T>(subject: T): string {
        if (subject instanceof HttpErrorResponse) {
            let id = '';
            let message = '';

            if (subject.status === 400) {
                message = subject.error.message;
                if (subject.error.modelState != null) {
                    message = subject.error.modelState.request[0];
                }

                const preface = `${this.configService.config.productName} API Validation Failed:`;
                id = this.addMessage(Severity.Danger, message, preface);
            } else {
                // Assume it's an unhandled .NET exception.
                message = subject.message;

                // Special case for unhandled .NET exceptions.
                if (subject.error.message) {
                    const ex = this.cleanExceptionMessage(subject.error.exceptionMessage);
                    message = `${subject.error.exceptionType}: ${subject.error.message} ${ex}`;
                    let innerException = subject.error.innerException;

                    while (innerException) {
                        message += ` ${this.cleanExceptionMessage(innerException.exceptionMessage)}`;
                        innerException = innerException.innerException;
                    }
                }

                const preface = `${this.configService.config.productName} API Error:`;
                id = this.addMessage(Severity.Danger, message, preface);
            }

            return id;
        } else {
            throw subject;
        }
    }

    clear(id: string): void {
        if (id) {
            FlashMessagesService.flashMessages = filter(this.flashMessages, (message: FlashMessage) => {
                return message.id !== id;
            });
        }
    }

    // noinspection JSMethodCanBeStatic
    clearAll(): void {
        FlashMessagesService.flashMessages = [];
    }

    hasMessagesOf(severity: Severity): boolean {
        return some(this.flashMessages, this.whichHave(severity));
    }

    messagesFor(severity: Severity): FlashMessage[] {
        return filter(this.flashMessages, this.whichHave(severity));
    }

    // noinspection JSMethodCanBeStatic
    private cleanExceptionMessage(exceptionMessage: string): string {
        if (exceptionMessage.endsWith('.')) {
            return exceptionMessage;
        }

        return `${exceptionMessage}.`;
    }

    private whichHave(severity: Severity): (message: FlashMessage) => boolean {
        return (message: FlashMessage) => {
            return message.severity === severity;
        };
    }
}
